import { useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RefreshCcw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	DetailFieldsGrid,
	DetailField
} from '@/components/ui/details-panel';
import type { VehicleJourneyLogItem } from './types';
import { PARKING_SESSION_JOURNEY_LOGS_QUERY } from './parking-sessions.gql';
import { formatDateTime } from './utils';

interface JourneyLogsQueryResponse {
	vehicleJourneyLogs?: {
		nodes?: Array<{
			vehicleJourneyLogId: string;
			eventTime: string;
			externalEventId: string;
			additionalData?: string | null;
			vehicleJourneyEventType?: { name?: string | null } | null;
			terminal?: {
				name?: string | null;
				terminalType?: { name?: string | null } | null;
			} | null;
			cashierShift?: {
				user?: { firstName?: string | null; lastName?: string | null } | null;
			} | null;
		}> | null;
	} | null;
}

interface ParkingSessionJourneyLogsProps {
	sessionId: string;
	isActive: boolean;
}

const mapNodesToLogs = (
	connection?: JourneyLogsQueryResponse['vehicleJourneyLogs']
) =>
	(connection?.nodes ?? []).map<VehicleJourneyLogItem>((log) => ({
		vehicleJourneyLogId: log.vehicleJourneyLogId,
		eventTime: log.eventTime,
		eventTypeName: log.vehicleJourneyEventType?.name ?? 'Unknown',
		terminalName: log.terminal?.name ?? 'Unknown',
		terminalType: log.terminal?.terminalType?.name ?? 'Unknown',
		cashierName:
			log.cashierShift?.user?.firstName && log.cashierShift?.user?.lastName
				? `${log.cashierShift.user.firstName} ${log.cashierShift.user.lastName}`
				: undefined,
		externalEventId: log.externalEventId,
		additionalData: log.additionalData
	}));

const formatAdditionalData = (payload?: string | null) => {
	if (!payload) return null;

	try {
		const parsed = JSON.parse(payload);
		// Return a readable string from the parsed data
		if (typeof parsed === 'object' && parsed !== null) {
			return Object.entries(parsed)
				.map(([key, value]) => `${key}: ${value}`)
				.join(', ');
		}
		return String(parsed);
	} catch {
		return payload;
	}
};

export const ParkingSessionJourneyLogs = ({
	sessionId,
	isActive
}: ParkingSessionJourneyLogsProps) => {
	const { t } = useTranslation('parkingSessions');

	const [fetchLogs, { data, loading, error, called }] =
		useLazyQuery<JourneyLogsQueryResponse>(PARKING_SESSION_JOURNEY_LOGS_QUERY, {
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all'
		});

	useEffect(() => {
		if (!isActive) return;

		fetchLogs({
			variables: { parkingSessionId: sessionId }
		});
	}, [fetchLogs, isActive, sessionId]);

	const logs = useMemo(
		() => mapNodesToLogs(data?.vehicleJourneyLogs),
		[data?.vehicleJourneyLogs]
	);

	const isLoading = loading || !called;

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 text-sm text-muted-foreground">
				<Loader2 className="w-4 h-4 animate-spin" />
				<span>{t('journeyLogs.loading')}</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col gap-3">
				<div className="flex items-center gap-2 text-sm text-destructive">
					<AlertTriangle className="w-4 h-4" />
					<span>{t('journeyLogs.error')}</span>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="w-fit"
					onClick={() =>
						fetchLogs({
							variables: { parkingSessionId: sessionId },
							fetchPolicy: 'network-only'
						})
					}
				>
					<RefreshCcw className="w-4 h-4 mr-2" />
					{t('journeyLogs.retry')}
				</Button>
			</div>
		);
	}

	if (logs.length === 0) {
		return (
			<div className="text-sm text-muted-foreground">
				{t('journeyLogs.empty')}
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{logs.map((log) => (
				<div
					key={log.vehicleJourneyLogId}
					className="pb-4 border-b border-gray-100 last:border-b-0 last:pb-0 dark:border-slate-800"
				>
					<div className="flex items-center justify-between mb-3">
						<div className="flex items-center gap-2">
							<Badge variant="outline" className="text-xs font-medium">
								{log.eventTypeName}
							</Badge>
							<span className="text-sm text-foreground">
								{formatDateTime(log.eventTime)}
							</span>
						</div>
						<span className="text-xs text-muted-foreground">
							{log.terminalName} ({log.terminalType})
						</span>
					</div>

					<DetailFieldsGrid>
						{log.cashierName && (
							<DetailField
								label={t('details.cashier')}
								value={log.cashierName}
							/>
						)}
					</DetailFieldsGrid>

					{log.additionalData && (
						<div className="mt-3 p-2 rounded-md bg-muted/50 text-xs text-muted-foreground">
							{formatAdditionalData(log.additionalData)}
						</div>
					)}
				</div>
			))}
		</div>
	);
};
